import { Injectable } from "@angular/core";
import { BehaviorSubject, map, Observable, tap } from "rxjs";
import { CurrentUser, DailyScoreRecord, VariantSignature } from "@model";
import { ScoreHttpService } from "./scores-http.service";
import { getTodaysDay, isDayToday, isNotNullOrUndefined } from "@helpers";
import { CurrentUserService } from "@core";

export const DEFAULT_DAILY_POINTS = 500;

@Injectable({ providedIn: "root" })
export class DailyScoreService {
  private _currentDailyScore$ = new BehaviorSubject<DailyScoreRecord | null>(null);
  currentDailyScore$ = this._currentDailyScore$.pipe(isNotNullOrUndefined());

  private _lastUpdateResultString = "";

  constructor(
    private _currentUserService: CurrentUserService,
    private _scoreHttpService: ScoreHttpService,
  ) {
    this._currentUserService.currentUser$.subscribe((user: CurrentUser | null) => {
      if (user) {
        this._scoreHttpService
          .getLastSavedDailyScore()
          .subscribe((lastSavedDailyScore) => {
            this._setCurrentDailyScoreRecord(lastSavedDailyScore);
          });
      } else {
        this._currentDailyScore$.next(null);
      }
    });
  }

  private _setCurrentDailyScoreRecord(lastSavedDailyScore: DailyScoreRecord) {
    const isCurrentDay = isDayToday(lastSavedDailyScore?.dailyScoreDay);

    if (isCurrentDay) {
      this._currentDailyScore$.next({
        dailyScoreDay: lastSavedDailyScore.dailyScoreDay,
        dailyScorePoints: lastSavedDailyScore.dailyScorePoints,
        dailyScoreGoal: lastSavedDailyScore.dailyScoreGoal ?? DEFAULT_DAILY_POINTS,
      });
    } else {
      this._currentDailyScore$.next({
        dailyScoreDay: getTodaysDay(),
        dailyScorePoints: 0,
        dailyScoreGoal: lastSavedDailyScore?.dailyScoreGoal ?? DEFAULT_DAILY_POINTS,
      });
    }
  }

  setLastResult(variantSignature: VariantSignature, result: number, resultDelta: number): void {
    const { category_id, level_id, variant_id } = variantSignature;
    const lastResultString = `${category_id}-${level_id}-${variant_id}-${result}`;
    const lastDailyScore = this._currentDailyScore$.value;

    if (lastResultString !== this._lastUpdateResultString && lastDailyScore) {
      this._updateDailyScore(lastDailyScore, resultDelta);
    }
    this._lastUpdateResultString = lastResultString;
  }

  private _updateDailyScore(lastDailyScore: DailyScoreRecord, resultDelta: number): void {
    const todaysDay = getTodaysDay();
    if (todaysDay === lastDailyScore?.dailyScoreDay) {
      this._currentDailyScore$.next({
        ...lastDailyScore,
        dailyScorePoints: lastDailyScore.dailyScorePoints + resultDelta,
      });
    } else {
      this._currentDailyScore$.next({
        ...lastDailyScore,
        dailyScoreDay: todaysDay,
        dailyScorePoints: resultDelta,
      });
    }
  }

  checkDay() {
    const todaysDay = getTodaysDay();
    const lastDailyScore = this._currentDailyScore$.value;

    if (lastDailyScore && todaysDay !== lastDailyScore.dailyScoreDay) {
      this._currentDailyScore$.next({
        ...lastDailyScore,
        dailyScoreDay: todaysDay,
        dailyScorePoints: 0,
      });
    }
  }

  setNewGoal(goal: number): Observable<null> {
    return this._scoreHttpService.setDailyScoreGoal(goal).pipe(
      tap((updatedRecord: DailyScoreRecord) => {
        this._currentDailyScore$.next(updatedRecord);
      }),
      map(() => null),
    );
  }
}
